// PopupWidget.jsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, useTheme, Link, ButtonBase, IconButton, List, ListItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';

const ELTERNERLAUBNIS_POPUP_COOKIE = 'elternerlaubnisPopupClosed';

const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const target = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieParts = decodedCookie.split(';');

  for (let i = 0; i < cookieParts.length; i += 1) {
    const cookie = cookieParts[i].trim();
    if (cookie.indexOf(target) === 0) {
      return cookie.substring(target.length, cookie.length);
    }
  }
  return null;
};

const PopupWidgetHome = () => {
  const theme = useTheme();

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const popupClosed = getCookie(ELTERNERLAUBNIS_POPUP_COOKIE);
    if (popupClosed) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShowPopup(false);
  };

  const handleAgeConfirmed = () => {
    setCookie(ELTERNERLAUBNIS_POPUP_COOKIE, 'true', 365);
    setShowPopup(false);
  };

  if (!showPopup) {
    return null; // Nothing to render if popup is closed or the cookie is set
  }

  return (
    <Box
      // This Box is the dark, blurry overlay
      sx={{
        position: 'fixed',
        zIndex: 9999,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        opacity: 0,
        animation: 'overlayFadeIn 0.3s ease-out forwards',
        '@keyframes overlayFadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      {/* This Box is the actual popup */}
      <Box
        sx={{
          width: { xs: '95%', sm: '90%' },
          paddingRight: '2.5rem !important',
          paddingLeft:'2.5rem !important',
          maxWidth: '800px',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: 24,
          paddingTop: '2.5rem !important',
          paddingBottom: '2.5rem !important',
          textAlign: 'center',
          // Scale transition
          transform: 'scale(0.8) translateY(20px)',
          opacity: 0,
          animation: 'popupAppear 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          '@keyframes popupAppear': {
            '0%': {
              transform: 'scale(0.8) translateY(20px)',
              opacity: 0,
            },
            '100%': {
              transform: 'scale(1) translateY(0px)',
              opacity: 1,
            },
          },
        }}
      >
        {/* Close button at the top right */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: theme.palette.text.primary
          }}
          aria-label="close popup"
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h2" sx={{mb: 8  }}>
          Hier deine Elternerlaubnis abholen!
        </Typography>
        {/* Button as Image with Text Overlay */}
        <Box sx={{ display: 'flex', justifyContent: 'center',marginTop: '20px' }}>
          <Box
            component={RouterLink}
            to="/wettbewerb/erlaubnis"
            sx={{
              cursor: 'pointer',
              maxWidth: '228.5px',
              position: 'relative',
              display: 'inline-block',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            <Box
              component="img"
              src="/assets/misc/Button.png"
              alt="Zu Instagram"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                fontFamily: 'Montserrat',
                fontStyle: 'italic',
                fontWeight: 'bold',
                textAlign: 'center',
                pointerEvents: 'none',
                fontSize: '1.5rem',
                whiteSpace: 'nowrap',
                width: '90%',
              }}
            >
              Hier klicken
            </Typography>
          </Box>
        </Box>
        <Button
          variant="text"
          onClick={handleAgeConfirmed}
          sx={{
            mt: 2,
            color: theme.palette.primary.main,
            textTransform: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          Hiermit bestätige ich, dass ich volljährig bin.
        </Button>
      </Box>
    </Box>
  );
};

export default PopupWidgetHome;
